import { SeoOptionsInterface } from '@wdpr/ra-angular-seo-metadata';
import { environment } from 'environments/environment';

export const CONFIG_CONSTANTS = {
    apiUrlKey: 'webApiUrl',
    cacheBuster: 'cacheBuster',
    cdnListKey: 'cdnList',
    cdnPathKey: 'cdnPath',
    defaultVersionKey: 'webApiDefaultVersion',
    developmentEnv: 'development',
    envKey: 'environment',
    oneIdKey: 'oneId',
    passholderLinkRoute: 'affiliations.passholderLinkRoute',
    prodEnvValue: 'prod',
    siteIdKey: 'siteId',
    svgPathKey: 'svgPath',
    watcher: 'watcher',
    disneyId: 'disneyId',
};

const explorerServicePath = 'explorer-service';
const keyringServicePath = 'keyring-service';
const searchServicePath = 'search-service';
const profileServicePath = 'profile-service';

export const LOGIN_URL = '/login';

// Keep the API_CONSTANTS sorted.
export const API_CONSTANTS = {
    authEndpoint: 'authz/public',
    authStatusEndpoint: 'authentication/status',
    calendarData: `${explorerServicePath}/calendar`,
    campaignEndpoint: `${explorerServicePath}/campaign-entity-simple`,
    createPersonalScheduleItemEndpoint: `${keyringServicePath}/personal-schedule-item`,
    currencyExchangeRateEndpoint: 'convert-currency/exchange-rate',
    detailsEndpoint: `${explorerServicePath}/details-entity-simple`,
    detailsMealPeriodScheduleEndpoint: `${explorerServicePath}/meal-periods-schedule`,
    detailsScheduleEndpoint: `${explorerServicePath}/details-entity-schedule`,
    diningAvailabilityCalendarDaysEndpoint: `${keyringServicePath}/dining-availability-calendar-days`,
    diningAvailabilityCalendarDaysApiEndpoint: `dining-availability-calendar-days`,
    diningAvailabilityEndpoint: `${explorerServicePath}/dining-availability`,
    diningAvailabilityEndpointList: `${explorerServicePath}/dining-availability-list`,
    friendsAndFamilyEndpoint: `${keyringServicePath}/friends-and-family`,
    jwtCookieName: 'pep_jwt_token',
    listAncestorEntitiesEndpoint: `${explorerServicePath}/list-ancestor-entities`,
    livePersonEndpoint: `${profileServicePath}/live-person`,
    listServiceEndpoint: 'list-service/details-slugs',
    mediaListEndpoint: 'media-list',
    mediaServiceEndpoint: 'media-service/media-engine-bundles',
    monthlyAvailabilityEndpoint: `${explorerServicePath}/monthly-availability`,
    personalizedContentEndpoint: 'recommendation-service/personalized-content',
    reservationConflictingItemsEndpoint: 'reservation-service/conflicting-items',
    resourceBundleEndpoint: 'language-bundles',
    searchQueryEndpoint: `${searchServicePath}/search`,
    searchSelectionHistoryEndpoint: `${searchServicePath}/search-history`,
    soloBaseEndpoint: 'solo',
    webApiVersionEndpoint: 'version',
};

// Any locale date formats should be in lower case without dashes or underscores
export const SCHEDULE_CONSTANTS = {
    // see https://devhints.io/moment for explanation of the moment formatting values
    shortDateFormat: 'ddd, MMM D, YYYY',
    fullDateFormat: 'dddd, MMMM D, YYYY',
    scheduleDateFormat: 'YYYY-MM-DD',
    timeOutputFormat: 'h:mm A',
    timeParseFormat: 'HH:mm:ss',
    scheduleTimesThreshold: 3,

    // WDW formats
    ptbrFullDateFormat: 'dddd, D [de] MMMM [de] YYYY',

    // HKDL formats
    idFullDateFormat: 'dddd, D MMMM YYYY',
    jaFullDateFormat: 'YYYY年 MMMM D日 dddd',
    koFullDateFormat: 'dddd, YYYY년 MMMM D일',
    msFullDateFormat: 'dddd, D MMMM YYYY',
    thFullDateFormat: 'dddd D MMMM YYYY',
    zhhkFullDateFormat: 'dddd, YYYY年M月D日',
    zhcnFullDateFormat: 'dddd, YYYY年M月D日'
};

export const SCHEDULE_TYPES = {
    operating: 'Operating',
    closedSchedule: 'Closed',
    refurbishment: 'Refurbishment',
    noPerformance: 'No Performance',
    performanceTime: 'Performance Time',
    extraMagicHours: 'Extra Magic Hours',
    specialTicketedEvent: 'Special Ticketed Event',
    extraMagicHourMagicMorning: 'Extra Magic Hour and Magic Morning',
    earlyEntry: 'Early Entry',
    extendedEvening: 'Extended Evening'
};

export const MEDIA_ENGINE_CONSTANTS = {
    selectors: {
        titleBarClass: 'me-title-bar',
        titleClass: 'me-title',
        ctaBtnClass: 'me-cta-btn',
        fsBtnClass: 'me-fs-btn',
        headerMeFsClass: 'me-fullscreen',
        headerClass: 'finder-syndicated-header-wrapper',
    },
    configOverrides: {
        video: 'configOverrides.config.video',
        slides: 'configOverrides.config.slides'
    },
    defaultTheme: 'snowball-hover-thumbs',
    desktop: 'desktop',
    mobileWeb: 'mobileWeb',
};

export const DESTINATION_ID_CONSTANTS = {
    wdw: '80007798;entityType=destination',
    dlr: '80008297;entityType=destination',
    hkdl: 'hkdl;entityType=destination'
};

export const DESTINATION_NAME_CONSTANTS = {
    wdw: 'Walt Disney World',
    dlr: 'Disneyland Resort',
    hkdl: 'Hong Kong Disneyland Resort'
};

export const MEAL_PERIOD_CONSTANTS = {
    mealPeriodsOrder: [
        'breakfast',
        'brunch',
        'lunch',
        'dinner',
        'lounge',
        'late-night',
    ],
    /**
     * This meal period is separate because we only want it to show in AAG and not as a
     * bookable meal period. This meal period has operating hours and appears bookable
     * but it's not. If you use it to search for a reservation it will return "nothing available".
     */
    lateNightDining: 'late-night-dining',
};

export const LOCALES = {
    en_US: 'en_US', // United States
    en_us_normalized: 'en-us',
    'es-us': 'es-us', // Spanish in US
    en_CA: 'en_CA', // Canadian in US
    en_ca_normalized: 'en-ca', // Canada normalized
    fr_ca: 'fr-ca', // French Canadian
    es_ar: 'es-ar', // Argentina
    es_mx: 'es-mx', // Mexico
    en_eu: 'en-eu', // Europe
    en_GB: 'en_GB', // UK
    en_GB_normalized: 'en-gb', // UK
    en_IE: 'en_IE', // Ireland
    jp: 'jp',       // Japan
    es_pe: 'es-pe', // Peru
    es_co: 'es-co', // Colombia
    es_cl: 'es-cl', // Chile
    pt_br: 'pt-br', // Brasil
    'zh-hk': 'zh-hk', // traditional Chinese
    'zh-cn': 'zh-cn', // simplified Chinese
    ja: 'ja',
    ko: 'ko',         // korean
    th: 'th',
    id: 'id',         // Bahasa Indonesia
    ms: 'ms',         // Bahasa Malaysia
};

export const LOCALE_DEFAULT = environment['LOCALE_DEFAULT'] || LOCALES.en_US;
export const LOCALE_NORMALIZED_DEFAULT = environment['LOCALE_NORMALIZED_DEFAULT'] || LOCALES.en_us_normalized;
export const LOCALE_HREF_PREFIX_IGNORELIST = [
    LOCALES.en_us_normalized,
    LOCALES.en_GB_normalized,
    LOCALES.en_eu
];

/**
 * Regexp used to match the LACD locales.
 * Matches: es-us, en_GB, pt-br, jp, en and non-standard locales like EN_US, Fr-CA, etc.
 * Does not match: esUS
 *
 * If you update this regex you should update this
 * src/static/server.ts: getLocale
 */
export const LOCALE_REGEXP = {
    lacd: /^[a-zA-Z]{2}((_|-)[a-zA-Z]{2})?$/g,
};

export const FINDER_TOGGLES_COOKIE_NAME = '__ft';

export const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

export const CAMPAIGN_CONSTANTS = {
    isCampaign: 'isCampaign',
    isOverview: 'isOverview',
    themeKey: 'themeData',
};

export const APP_NAME_HEADER = {
    name: 'X-Disney-App-Name',
    value: 'advanced-finder'
};

export const SEO_OPTIONS: SeoOptionsInterface = {
    useTranslate: false,
    serverPath: '/seo',
    manualBootstrap: false,
    includeMetaTag: {
        alternate: false,
    },
    customSeoRetrieval: true,
};

export const TRAVEL_AGENT = {
    cookieName : 'SHARED_SESSION_LINK',
    prefix: 'reservations',
    store: 'dta-packages-std',
    caStore: 'dta-packages-std-ca',
    wdwTicketsStore: 'travel-agents',
    dlrTicketsStore: 'agent',
    header: 'x-disney-travel-agent-store'
};

export const CALENDAR_CONSTANTS = {
    sessionStorage : {
        selectedDate: 'calendarSelectedDate',
        selectedFilters: 'calendarSelectedFilters'
    }
};

export const ANALYTICS_TIMEOUT = 2000;

export const ANALYTICS_FACETS_UTILIZED_ACTION = {
    add: 'Add',
    remove: 'Remove'
};
